import React, { useState } from 'react';
import type { AdminTwin } from '@repo/frontend-types';

import DraftStatusTag from './DraftStatusTag';
import OnboardingHeader from './OnboardingHeader';
import OnboardingNav from './OnboardingNav';
import OnboardingTwinCreate from './OnboardingTwinCreate';
import { createModeNavItems } from './createModeNav';

interface Props {
  organisationId: number;
  orgBid: string;
  organisationName?: string;
  /** The wizard's Launch label, so the locked list names Launch as the wizard does. */
  launchLabel: string;
  getToken: () => Promise<string>;
  onCreated: (twin: AdminTwin) => void;
  onGoToTwins: () => void;
  /**
   * The washed map has drawn behind the form, so the content lets it show
   * (`onboarding-persistent-scene-twin-details-map`, R1.1), as the wizard's washed steps do.
   */
  washed?: boolean;
}

const noop = () => undefined;

/**
 * The wizard in **create mode** (`twin-details-page-create`, R2; *Twin Details Page* frame 01): the
 * nav bar's *New twin* chip, then Twin details' title, its status line and *Not created yet*
 * (`onboarding-persistent-scene-step-titles`), the whole step list with every step after Twin
 * details locked, and the create form.
 *
 * Rendered by `OnboardingPage` as an early return INSIDE its own styled root, so it reuses the
 * page's layout classes (`.columns`, `.content`, `.save-slot`) rather than copying them — and so none of the wizard's twin-scoped reads or effects
 * (draft, hours, launch record, plan, register) is ever mounted without a twin. Its own state, the
 * collapsed step list, lives here so the page's hook order is unchanged by the early return.
 *
 * The page draws it in the wizard's frame, over the washed map at the organisation's location
 * (`onboarding-persistent-scene-twin-details-map`, R1.1): the glass bar, and the content clear once
 * the map has drawn (`washed`).
 */
const OnboardingCreateMode: React.FC<Props> = ({
  organisationId,
  orgBid,
  organisationName,
  launchLabel,
  getToken,
  onCreated,
  onGoToTwins,
  washed = false,
}) => {
  // Frame 01 draws the list collapsed; the person can widen it.
  const [collapsed, setCollapsed] = useState(true);
  return (
    <>
      <OnboardingHeader
        surface="glass"
        route="create"
        twinName="New twin"
        stepTitle="Twin details"
        statusLine="Saved to the twin, not launched"
        actions={
          <div className="save-slot draft-slot">
            <DraftStatusTag
              notCreated
              subject="twin"
              status="saved"
              savedAt={null}
              reason={null}
              onRetry={noop}
            />
          </div>
        }
      />
      <div className="columns">
        <OnboardingNav
          items={createModeNavItems(launchLabel)}
          current="twinDetails"
          onSelect={noop}
          collapsed={collapsed}
          onToggleCollapsed={() => setCollapsed(c => !c)}
        />
        <section className={`content${washed ? ' is-washed' : ''}`}>
          <div className="content-body">
            <OnboardingTwinCreate
              organisationId={organisationId}
              orgBid={orgBid}
              organisationName={organisationName}
              getToken={getToken}
              onCreated={onCreated}
              onGoToTwins={onGoToTwins}
            />
          </div>
        </section>
      </div>
    </>
  );
};

export default OnboardingCreateMode;
